import { chordTones, isInKey, spellInKey } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { noteLabel } from './practice-target.js'

/**
 * How "sung as …" spells the note sung instead of the target (Bowie, on M6a):
 * - a note of the key, or for chord-only material a tone of the chord, takes that spelling;
 * - anything else is spelled by the direction of the miss, flat when sung below the target and sharp when
 *   above, so E4 sung low in C reads E♭4: the singer sat flat on their own note. Never a double accidental.
 */

const sharpNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
const flatNames = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B']

export interface SungNoteContext {
  /** The key the target is spelled in; absent for chord-only material */
  key?: Scale
  /** The chord the target note sits on, for chord-only material */
  chord?: string
  /** Sung below the target */
  isBelow: boolean
}

export const sungNoteName = (midi: number, { key, chord, isBelow }: SungNoteContext): string => {
  const pitchClass = ((midi % 12) + 12) % 12
  const own = (() => {
    if (key) {
      const name = spellInKey(pitchClass, key)
      return isInKey(name, key) ? name : undefined
    }
    return chord ? chordTones(chord)?.find(tone => tone.pitchClass === pitchClass)?.name : undefined
  })()
  return noteLabel(own ?? (isBelow ? flatNames : sharpNames)[pitchClass], midi)
}
